// src/services/firebaseStorage.js
import { 
    collection, 
    doc, 
    getDoc, 
    setDoc, 
    updateDoc,
    deleteDoc, 
    query, 
    where, 
    getDocs,
    arrayUnion,
    arrayRemove,
    writeBatch
  } from 'firebase/firestore';
  import { db } from '../services/firebase';
  
  /**
   * Firebase Storage Service
   * Replaces window.storage with Firestore database
   * Maintains same interface for backward compatibility
   */
  
  class FirebaseStorage {
    /**
     * Get a document from Firestore
     * @param {string} key - Format: "collection:id" (e.g., "club:abc123")
     * @returns {Promise<{value: string} | null>}
     */
    async get(key) {
      try {
        const [collectionName, docId] = this.parseKey(key);
        const docRef = doc(db, collectionName, docId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          return {
            value: JSON.stringify(docSnap.data())
          };
        }
        return null;
      } catch (error) {
        console.error(`Error getting ${key}:`, error);
        return null;
      }
    }
  
    /**
     * Set a document in Firestore
     * @param {string} key - Format: "collection:id"
     * @param {string} value - JSON string of data
     */
    async set(key, value) {
      try {
        const [collectionName, docId] = this.parseKey(key);
        const data = JSON.parse(value);
        
        // Add metadata
        const docData = {
          ...data,
          updatedAt: new Date().toISOString()
        };
        
        const docRef = doc(db, collectionName, docId);
        await setDoc(docRef, docData, { merge: true });
        
        return true;
      } catch (error) {
        console.error(`Error setting ${key}:`, error);
        throw error;
      }
    }
  
    /**
     * Delete a document from Firestore
     * @param {string} key - Format: "collection:id"
     */
    async delete(key) {
      try {
        const [collectionName, docId] = this.parseKey(key);
        const docRef = doc(db, collectionName, docId);
        await deleteDoc(docRef);
        return true;
      } catch (error) {
        console.error(`Error deleting ${key}:`, error);
        throw error;
      }
    }
  
    /**
     * Parse key into collection and document ID
     * @param {string} key - Format: "collection:id"
     * @returns {[string, string]} - [collectionName, docId]
     */
    parseKey(key) {
      const parts = key.split(':');
      if (parts.length < 2) {
        throw new Error(`Invalid key format: ${key}. Expected "collection:id"`);
      }
      
      const collectionName = parts[0];
      const docId = parts.slice(1).join(':'); // Handle IDs with colons
      
      return [collectionName, docId];
    }
  
    /**
     * Get all documents from a collection
     * @param {string} collectionName - Name of the collection
     * @returns {Promise<Array>}
     */
    async getAllFromCollection(collectionName) {
      try {
        const querySnapshot = await getDocs(collection(db, collectionName));
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error(`Error getting all from ${collectionName}:`, error);
        return [];
      }
    }
  
    /**
     * Query documents with a condition
     * @param {string} collectionName
     * @param {string} field
     * @param {string} operator
     * @param {any} value
     */
    async query(collectionName, field, operator, value) {
      try {
        const q = query(
          collection(db, collectionName), 
          where(field, operator, value)
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error(`Error querying ${collectionName}:`, error);
        return [];
      }
    }
  
    /**
     * Add item to array field
     * @param {string} key - Document key
     * @param {string} field - Array field name
     * @param {any} value - Value to add
     */
    async arrayAdd(key, field, value) {
      try {
        const [collectionName, docId] = this.parseKey(key);
        const docRef = doc(db, collectionName, docId);
        await updateDoc(docRef, {
          [field]: arrayUnion(value),
          updatedAt: new Date().toISOString()
        });
        return true;
      } catch (error) {
        console.error(`Error adding to array in ${key}:`, error);
        throw error;
      }
    }
  
    /**
     * Remove item from array field
     * @param {string} key - Document key
     * @param {string} field - Array field name
     * @param {any} value - Value to remove
     */
    async arrayRemove(key, field, value) {
      try {
        const [collectionName, docId] = this.parseKey(key);
        const docRef = doc(db, collectionName, docId);
        await updateDoc(docRef, {
          [field]: arrayRemove(value),
          updatedAt: new Date().toISOString()
        });
        return true;
      } catch (error) {
        console.error(`Error removing from array in ${key}:`, error);
        throw error;
      }
    }
  
    /**
     * Batch operations for multiple writes
     * @param {Array} operations - Array of {type, key, value}
     */
    async batch(operations) {
      try {
        const batch = writeBatch(db);
        
        for (const op of operations) {
          const [collectionName, docId] = this.parseKey(op.key);
          const docRef = doc(db, collectionName, docId);
          
          if (op.type === 'set') {
            const data = JSON.parse(op.value);
            batch.set(docRef, {
              ...data,
              updatedAt: new Date().toISOString()
            });
          } else if (op.type === 'delete') {
            batch.delete(docRef);
          } else if (op.type === 'update') {
            batch.update(docRef, {
              ...op.updates,
              updatedAt: new Date().toISOString()
            });
          }
        }
        
        await batch.commit();
        return true;
      } catch (error) {
        console.error('Error in batch operation:', error);
        throw error;
      }
    }
  }
  
  // Create singleton instance
  const firebaseStorage = new FirebaseStorage();
  
  // Export as both default and named export for flexibility
  export default firebaseStorage;
  export { firebaseStorage };